import React, { useMemo, useState } from 'react';
import { Button, Input, Select, Textarea } from '../../ui/components';
import { useT } from '../../i18n';
import { BUDGET_PERIOD_TYPES } from './budgetsApi';

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Derive period start/end/label from the chosen cadence + anchor inputs.
function derivePeriod(type, anchor) {
  const year = Number(anchor.year) || new Date().getFullYear();
  if (type === 'Monthly') {
    const [y, m] = (anchor.month || `${year}-01`).split('-').map(Number);
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    return { start: iso(start), end: iso(end), label: `${y}-${pad(m)}` };
  }
  if (type === 'Quarterly') {
    const q = Number(anchor.quarter) || 1;
    const start = new Date(year, (q - 1) * 3, 1);
    const end = new Date(year, q * 3, 0);
    return { start: iso(start), end: iso(end), label: `Q${q} ${year}` };
  }
  if (type === 'Annual') {
    return { start: iso(new Date(year, 0, 1)), end: iso(new Date(year, 11, 31)), label: `FY${year}` };
  }
  // Custom
  return { start: anchor.start || '', end: anchor.end || '', label: anchor.label || '' };
}

export default function BudgetForm({ budget, properties, costCenters, glAccounts, currencies, submitting, onSubmit, onCancel }) {
  const t = useT();
  const editing = Boolean(budget);
  const now = new Date();

  const [propertyId, setPropertyId] = useState(budget?.propertyId || '');
  const [costCenter, setCostCenter] = useState(budget?.costCenter || (costCenters[0]?.code ?? ''));
  const [glAccountCode, setGlAccountCode] = useState(budget?.glAccountCode || '');
  const [periodType, setPeriodType] = useState(budget?.periodType || 'Monthly');
  const [amount, setAmount] = useState(budget?.amount?.amount ?? '');
  const [currency, setCurrency] = useState(budget?.amount?.currency || currencies[0] || 'USD');
  const [notes, setNotes] = useState(budget?.notes || '');

  const [anchor, setAnchor] = useState(() => ({
    month: budget?.periodType === 'Monthly' && budget?.periodStart ? budget.periodStart.slice(0, 7) : `${now.getFullYear()}-${pad(now.getMonth() + 1)}`,
    year: budget?.periodStart ? new Date(budget.periodStart).getFullYear() : now.getFullYear(),
    quarter: budget?.periodType === 'Quarterly' && budget?.periodStart ? Math.floor(new Date(budget.periodStart).getMonth() / 3) + 1 : 1,
    start: budget?.periodStart ? budget.periodStart.slice(0, 10) : '',
    end: budget?.periodEnd ? budget.periodEnd.slice(0, 10) : '',
    label: budget?.periodType === 'Custom' ? budget?.periodLabel || '' : '',
  }));

  const setAnchorField = (patch) => setAnchor((a) => ({ ...a, ...patch }));

  const ccOptions = useMemo(
    () => [{ value: '', label: t('— Select —') }, ...costCenters.map((c) => ({ value: c.code, label: c.name ? `${c.code} · ${c.name}` : c.code }))],
    [costCenters, t],
  );
  const glOptions = useMemo(
    () => [{ value: '', label: t('— Whole cost centre —') }, ...glAccounts.map((g) => ({ value: g.code, label: g.name ? `${g.code} · ${g.name}` : g.code }))],
    [glAccounts, t],
  );
  const propOptions = useMemo(
    () => [{ value: '', label: t('All properties (organization)') }, ...properties.map((p) => ({ value: p.id, label: p.name }))],
    [properties, t],
  );
  const currencyOptions = useMemo(
    () => (currencies.length ? currencies : ['USD']).map((c) => ({ value: c, label: c })),
    [currencies],
  );
  const currentYear = now.getFullYear();
  const yearOptions = useMemo(
    () => [currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((v) => ({ value: String(v), label: String(v) })),
    [currentYear],
  );

  const period = derivePeriod(periodType, anchor);

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      propertyId: propertyId || null,
      costCenter,
      glAccountCode: glAccountCode || null,
      periodType,
      periodLabel: period.label,
      periodStart: period.start ? new Date(period.start).toISOString() : null,
      periodEnd: period.end ? new Date(period.end).toISOString() : null,
      amount: Number(amount) || 0,
      currency,
      notes: notes.trim() || null,
    });
  };

  return (
    <form className="bud-drawer" onSubmit={submit}>
      <div className="bud-drawer__grid">
        <div className="ps-field bud-drawer__full">
          <span className="ps-field__label">{t('Property')}</span>
          <Select value={propertyId} onChange={setPropertyId} options={propOptions} ariaLabel={t('Property')} searchable />
        </div>

        <div className="ps-field">
          <span className="ps-field__label">{t('Cost centre')}</span>
          <Select value={costCenter} onChange={setCostCenter} options={ccOptions} ariaLabel={t('Cost centre')} searchable />
        </div>
        <div className="ps-field">
          <span className="ps-field__label">{t('GL account')}</span>
          <Select value={glAccountCode} onChange={setGlAccountCode} options={glOptions} ariaLabel={t('GL account')} searchable />
        </div>

        <div className="ps-field">
          <span className="ps-field__label">{t('Period type')}</span>
          <Select value={periodType} onChange={setPeriodType} options={BUDGET_PERIOD_TYPES.map((o) => ({ ...o, label: t(o.label) }))} ariaLabel={t('Period type')} />
        </div>

        {periodType === 'Monthly' && (
          <Input label={t('Month')} type="month" value={anchor.month} onChange={(e) => setAnchorField({ month: e.target.value })} />
        )}
        {periodType === 'Quarterly' && (
          <>
            <div className="ps-field">
              <span className="ps-field__label">{t('Quarter')}</span>
              <Select
                value={String(anchor.quarter)}
                onChange={(v) => setAnchorField({ quarter: Number(v) })}
                options={[1, 2, 3, 4].map((q) => ({ value: String(q), label: `Q${q}` }))}
                ariaLabel={t('Quarter')}
              />
            </div>
            <div className="ps-field">
              <span className="ps-field__label">{t('Year')}</span>
              <Select value={String(anchor.year)} onChange={(v) => setAnchorField({ year: Number(v) })} options={yearOptions} ariaLabel={t('Year')} />
            </div>
          </>
        )}
        {periodType === 'Annual' && (
          <div className="ps-field">
            <span className="ps-field__label">{t('Year')}</span>
            <Select value={String(anchor.year)} onChange={(v) => setAnchorField({ year: Number(v) })} options={yearOptions} ariaLabel={t('Year')} />
          </div>
        )}
        {periodType === 'Custom' && (
          <>
            <Input label={t('Start')} type="date" value={anchor.start} onChange={(e) => setAnchorField({ start: e.target.value })} />
            <Input label={t('End')} type="date" value={anchor.end} onChange={(e) => setAnchorField({ end: e.target.value })} />
            <Input label={t('Label')} wrapClassName="bud-drawer__full" value={anchor.label} placeholder={t('e.g. Summer season')} onChange={(e) => setAnchorField({ label: e.target.value })} />
          </>
        )}

        <Input label={t('Amount')} type="number" value={amount} placeholder="0.00" onChange={(e) => setAmount(e.target.value)} />
        <div className="ps-field">
          <span className="ps-field__label">{t('Currency')}</span>
          <Select value={currency} onChange={setCurrency} options={currencyOptions} ariaLabel={t('Currency')} />
        </div>

        <Textarea label={t('Notes')} wrapClassName="bud-drawer__full" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('Optional context')} />
      </div>

      <p className="bud-panel__hint" style={{ marginTop: 'var(--space-3)' }}>
        {t('Period')}: <strong>{period.label || '—'}</strong> ({period.start || '?'} → {period.end || '?'})
      </p>

      <div className="set-form__actions" style={{ marginTop: 'var(--space-4)' }}>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>{t('Cancel')}</Button>
        <Button type="submit" variant="primary" loading={submitting}>{editing ? t('Save budget') : t('Create budget')}</Button>
      </div>
    </form>
  );
}
